import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaUsers, 
  FaRocket, 
  FaShieldAlt, 
  FaCog, 
  FaHandshake, 
  FaHeadset,
  FaAward,
  FaLightbulb,
  FaClock
} from 'react-icons/fa';
import './About.css';

const About = () => {
  const features = [
    {
      icon: <FaUsers />,
      title: "Expert Team",
      description: "Our team consists of highly skilled developers, designers, and project managers with years of industry experience.",
      color: "#6366f1"
    },
    {
      icon: <FaRocket />,
      title: "Fast Delivery",
      description: "We understand the importance of time in business. Our agile development process ensures quick turnaround times.",
      color: "#8b5cf6"
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Solutions",
      description: "Security is our top priority. We implement industry best practices to protect your data and applications.",
      color: "#ec4899"
    },
    {
      icon: <FaCog />,
      title: "Scalable Architecture",
      description: "We build solutions that grow with your business, ensuring long-term sustainability and performance.",
      color: "#f59e0b"
    },
    {
      icon: <FaHandshake />,
      title: "Client Partnership",
      description: "We work as your technology partner, not just a vendor. Your success is our success.",
      color: "#10b981"
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Support",
      description: "Our dedicated support team is always available to help you with any technical issues or questions.",
      color: "#ef4444"
    }
  ];

  const achievements = [
    {
      icon: <FaAward />,
      number: "78+",
      label: "Projects Delivered",
      description: "Successfully completed projects across various industries"
    },
    {
      icon: <FaUsers />,
      number: "250+",
      label: "Happy Clients",
      description: "Satisfied clients who trust us with their technology needs"
    },
    {
      icon: <FaLightbulb />,
      number: "50+",
      label: "Innovation Awards",
      description: "Recognition for our innovative solutions and excellence"
    },
    {
      icon: <FaClock />,
      number: "5+",
      label: "Years Experience",
      description: "Years of expertise in delivering cutting-edge solutions"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="about" className="about">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="section-badge">About Us</span>
          <h2 className="section-title">
            Why Choose <span className="gradient-text">Codexa Works</span>
          </h2>
          <p className="section-description">
            We are a leading technology company dedicated to delivering innovative solutions 
            that drive business growth and digital transformation.
          </p>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3>Transforming Ideas into Digital Reality</h3>
            <p>
              At Codexa Works, we believe in the power of technology to transform businesses 
              and create meaningful impact. Our team of passionate developers, designers, and 
              strategists work together to deliver solutions that not only meet your current needs 
              but also prepare you for future challenges.
            </p>
            <p>
              With over <b>12+ years</b> of experience in the industry, we have successfully delivered 
              hundreds of projects across various sectors including e-commerce, healthcare, 
              finance, and education. Our commitment to quality, innovation, and client satisfaction 
              has made us a trusted partner for businesses worldwide.
            </p>
            <div className="about-highlights">
              <div className="highlight">
                <span className="highlight-number">100%</span>
                <span className="highlight-text">Client Satisfaction Rate</span>
              </div>
              <div className="highlight">
                <span className="highlight-number">24/7</span>
                <span className="highlight-text">Support Available</span>
              </div>
              <div className="highlight">
                <span className="highlight-number">100%</span>
                <span className="highlight-text">On-Time Delivery</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="about-visual"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="visual-container">
              <div className="floating-element element-1">
                <FaRocket />
                <span>Innovation</span>
              </div>
              <div className="floating-element element-2">
                <FaShieldAlt />
                <span>Security</span>
              </div>
              <div className="floating-element element-3">
                <FaUsers />
                <span>Team</span>
              </div>
              <div className="main-visual">
                <div className="tech-circle">
                  <div className="tech-item">React.js</div>
                  <div className="tech-item">HTML</div>
                  <div className="tech-item">CSS</div>
                  <div className="tech-item">Shopify</div>
                  <div className="tech-item">WordPress</div>
                  <div className="tech-item">Node.js</div>
                  <div className="tech-item">AWS</div>
                  <div className="tech-item">Google Cloud</div>
                  <div className="tech-item">MongoDB</div>
                  <div className="tech-item">Docker</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h3 className="features-title">What Makes Us Different</h3>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <div 
                  className="feature-icon"
                  style={{ color: feature.color }}
                >
                  {feature.icon}
                </div>
                <h4 className="feature-title">{feature.title}</h4>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* <motion.div
          className="achievements-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="achievements-title">Our Achievements</h3>
          <div className="achievements-grid">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="achievement-card"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="achievement-icon">
                  {achievement.icon}
                </div>
                <h4 className="achievement-number">{achievement.number}</h4>
                <h5 className="achievement-label">{achievement.label}</h5>
                <p className="achievement-description">{achievement.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default About;

